import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { formatDate } from '../../../utils/formatters';

const ImportantDatesSection = ({ dates }) => {
  const { t } = useTranslation('conferences');
  const getStatus = (dateString) => {
    const today = new Date();
    const targetDate = new Date(dateString);
    
    if (targetDate < today) {
      return { label: t('conferenceLanding.statusCompleted'), color: 'text-muted-foreground', bg: 'bg-muted' };
    } else if (targetDate?.getTime() - today?.getTime() < 7 * 24 * 60 * 60 * 1000) {
      return { label: t('conferenceLanding.statusUpcoming'), color: 'text-warning', bg: 'bg-warning/10' };
    } else {
      return { label: t('conferenceLanding.statusScheduled'), color: 'text-primary', bg: 'bg-primary/10' };
    }
  };

  return (
    <section className="py-4 md:py-6 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-4 md:mb-5">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Clock" size={20} className="text-warning" />
          </div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground">{t('conferenceLanding.importantDates')}</h2>
        </div>

        <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
          <div className="divide-y divide-border">
            {dates?.map((date, index) => {
              const status = getStatus(date?.date);
              return (
                <div
                  key={index}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 md:gap-4 p-4 md:p-6 hover:bg-muted/30 transition-all duration-250"
                >
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={date?.icon} size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm md:text-base font-semibold text-foreground mb-1">
                        {date?.title}
                      </h3>
                      {date?.description && (
                        <p className="text-xs md:text-sm text-muted-foreground caption">
                          {date?.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 sm:flex-shrink-0">
                    <div className="text-right">
                      <p className="text-sm md:text-base font-medium text-foreground whitespace-nowrap">
                        {formatDate(date?.date, { month: 'long' })}
                      </p>
                      <p className="text-xs text-muted-foreground caption">{date?.time}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 ${status?.bg} ${status?.color} text-xs font-medium caption whitespace-nowrap`}>
                      {status?.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-4 md:mt-6 flex items-start gap-2 p-3 md:p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <Icon name="Info" size={18} className="text-primary mt-0.5 flex-shrink-0" />
          <p className="text-xs md:text-sm text-foreground/80 caption">
            {t('conferenceLanding.deadlinesNote')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ImportantDatesSection;